export type CustomerCompensationStatus = "scheduled" | "credited" | "used" | "expired" | "reversed";

export interface CustomerCompensation {
  id: string;
  failureMonth: string;
  status: CustomerCompensationStatus;
  conversations: number;
  remaining: number;
  credited: number;
  used: number;
  expired: number;
  reversed: number;
  validFrom: string | null;
  expiresAt: string | null;
  creditedAt: string | null;
  reversedAt: string | null;
}

export interface CompensationConsumption {
  compensationId: string;
  failureMonth: string;
  engagementSessionId: string | null;
  conversationId: string | null;
  sessionStart: string | null;
  paidAt: string;
}

export interface OrgCompensations {
  compensations: CustomerCompensation[];
  consumed: CompensationConsumption[];
  consumedTotal: number;
}

export const EMPTY_ORG_COMPENSATIONS: OrgCompensations = { compensations: [], consumed: [], consumedTotal: 0 };

const STATUSES: readonly CustomerCompensationStatus[] = ["scheduled", "credited", "used", "expired", "reversed"];

const num = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const str = (value: unknown) => (typeof value === "string" && value ? value : null);
const rec = (value: unknown) => (value && typeof value === "object" ? (value as Record<string, unknown>) : null);

export function parseOrgCompensations(raw: unknown): OrgCompensations {
  const root = rec(raw);
  if (!root) return EMPTY_ORG_COMPENSATIONS;
  const compensations = (Array.isArray(root.compensations) ? root.compensations : [])
    .map(rec)
    .filter((row): row is Record<string, unknown> => !!row && !!str(row.id) && STATUSES.includes(row.status as CustomerCompensationStatus))
    .map((row) => ({
      id: String(row.id),
      failureMonth: String(row.failure_month ?? ""),
      status: row.status as CustomerCompensationStatus,
      conversations: num(row.conversations),
      remaining: num(row.remaining),
      credited: num(row.credited),
      used: num(row.used),
      expired: num(row.expired),
      reversed: num(row.reversed),
      validFrom: str(row.valid_from),
      expiresAt: str(row.expires_at),
      creditedAt: str(row.credited_at),
      reversedAt: str(row.reversed_at),
    }));
  const consumed = (Array.isArray(root.consumed) ? root.consumed : [])
    .map(rec)
    .filter((row): row is Record<string, unknown> => !!row && !!str(row.compensation_id) && !!str(row.paid_at))
    .map((row) => ({
      compensationId: String(row.compensation_id),
      failureMonth: String(row.failure_month ?? ""),
      engagementSessionId: str(row.engagement_session_id),
      conversationId: str(row.conversation_id),
      sessionStart: str(row.session_start),
      paidAt: String(row.paid_at),
    }));
  return { compensations, consumed, consumedTotal: Math.max(num(root.consumed_total), consumed.length) };
}

export function displayStatus(compensation: CustomerCompensation, now: Date = new Date()): CustomerCompensationStatus {
  if (compensation.status === "credited" && compensation.expiresAt && Date.parse(compensation.expiresAt) <= now.getTime()) {
    return "expired";
  }
  return compensation.status;
}

export function availableBalance(data: OrgCompensations, now: Date = new Date()): number {
  return data.compensations
    .filter((c) => displayStatus(c, now) === "credited" && (!c.validFrom || Date.parse(c.validFrom) <= now.getTime()))
    .reduce((sum, c) => sum + c.remaining, 0);
}

export function remainingShown(compensation: CustomerCompensation, now: Date = new Date()): number {
  return displayStatus(compensation, now) === "credited" ? compensation.remaining : 0;
}

export function compensatedConversationIds(data: OrgCompensations): Set<string> {
  return new Set(data.consumed.map((c) => c.conversationId).filter((id): id is string => !!id));
}
